import { ConsumerDetails } from "./types";
import React, { useState, useEffect } from "react";
// Ensure React types for JSX are available
import type {} from "react/jsx-runtime";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Typography,
  Box,
  IconButton,
  Select,
  MenuItem,
  TextField,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { consumersApi } from "../../API/consumersApi";
import { Cancel, AddCard, Check, Clear } from "@mui/icons-material";
/* eslint-disable*/
interface CreditPurchaseProps {
  consumerDetails: ConsumerDetails;
  showPurchaseModal: boolean;
  setShowPurchaseModal: (open: boolean) => void;
  onCreditsPurchased?: (creditsPurchased: number) => void;
}
// Removed PaymentDetails interface since we redirect to Inveroak's form

export default function CreditPurchase(props: CreditPurchaseProps) {
  const { consumerDetails, showPurchaseModal, setShowPurchaseModal } = props;
  
  // Responsive breakpoints
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // < 600px
  
  const [selectedBundle, setSelectedBundle] = useState("");
  const [bundleDetails, setBundleDetails] = useState<any>(null);
  const [paymentResult, setPaymentResult] = useState<any>(null);
  const [currentState, setCurrentState] = useState("purchase-credit"); // Added state management
  // Removed paymentDetails and errors since we redirect to Inveroak's form
  const [creditBundles, setCreditBundles] = useState<any[]>([]);
  const [savedCards, setSavedCards] = useState<any[]>([]);

  useEffect(() => {
    setCurrentState("purchase-credit");
  }, []);

  useEffect(() => {
    async function fetchBundles() {
      if (consumerDetails?.clientId) {
        const result = await consumersApi.getCreditBundlesForClient(
          consumerDetails.clientId
        );
        if (Array.isArray(result)) {
          setCreditBundles(result);
        } else {
          setCreditBundles([]);
        }
      }
    }
    
    async function fetchSavedCards() {
      if (consumerDetails?.id) {
        try {
          const result = await consumersApi.getSavedCards(consumerDetails.id);
          if (Array.isArray(result)) {
            setSavedCards(result);
          } else {
            setSavedCards([]);
          }
        } catch (error) {
          console.error('Error fetching saved cards:', error);
          setSavedCards([]);
        }
      }
    }
    
    fetchBundles();
    fetchSavedCards();
  }, [consumerDetails?.clientId, consumerDetails?.id]);

  // Removed handleDetailsChange since we're not collecting card details

  // Payment redirect/result logic
  const [isProcessing, setIsProcessing] = useState(false);
  const [orderError, setOrderError] = useState("");

  const formatCurrencyAmount = (amount: number, currency?: string) => {
    const safeCurrency = (currency || "GBP").toUpperCase();
    const numericAmount = Number(amount || 0);

    try {
      return new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: safeCurrency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(numericAmount);
    } catch {
      return `${safeCurrency} ${numericAmount.toFixed(2)}`;
    }
  };

  // Helper: open payment URL in new tab
  const openPaymentWindow = (url: string) => {
    window.open(url, "_blank");
  };

  // Handle payment result (simulate for now)
  const handlePaymentResult = (result: "success" | "failed") => {
    setCurrentState(
      result === "success" ? "payment-success" : "payment-failed"
    );
    // If success, call the callback to animate credits
    if (
      result === "success" &&
      paymentResult?.creditsPurchased &&
      props.onCreditsPurchased
    ) {
      props.onCreditsPurchased(paymentResult.creditsPurchased as number);
    }
  };

  // Handle previous card payment
  const handleUsePreviousCard = async () => {
    if (!selectedBundle) {
      setOrderError("Please select a bundle first");
      return;
    }

    if (savedCards.length === 0) {
      setOrderError("No saved cards available");
      return;
    }

    setCurrentState("loading");
    setIsProcessing(true);
    setOrderError("");
    
    // Get bundle details for dynamic display
    const bundle = creditBundles.find((b: any) => b.id === selectedBundle);
    setBundleDetails(bundle || null);
    
    try {
      // Use the first saved card (or you can let user select)
      const savedCard = savedCards[0];
      
      const payload = {
        consumerId: consumerDetails.id,
        clientId: consumerDetails.clientId,
        bundleId: selectedBundle,
        recurringToken: savedCard.recurringToken,
      };
      
      const result = await consumersApi.usePreviousCard(payload);
      console.log('Use previous card result:', result);
      
      if (result && result.success) {
        setPaymentResult({
          creditsPurchased: bundle?.credits || 0,
          orderId: result.orderid,
          status: result.status
        });
        setCurrentState("payment-success");
        if (props.onCreditsPurchased && bundle?.credits) {
          props.onCreditsPurchased(bundle.credits);
        }
      } else {
        setOrderError(result?.message || "Previous card payment failed");
        setCurrentState("payment-failed");
      }
    } catch (err: any) {
      console.error('Previous card payment failed:', err);
      setOrderError(err.message || "Previous card payment failed");
      setCurrentState("payment-failed");
    }
    setIsProcessing(false);
  };

  // Create payment order and redirect to booking URL (no card details needed)
  const handlePayWithNewCard = async () => {
    if (!selectedBundle) {
      setOrderError("Please select a bundle first");
      return;
    }

    setCurrentState("loading");
    setIsProcessing(true);
    setOrderError("");
    
    // Get bundle details for dynamic display
    const bundle = creditBundles.find((b: any) => b.id === selectedBundle);
    setBundleDetails(bundle || null);
    
    try {
      // Call backend to create order (no card details needed)
      const payload = {
        consumerId: consumerDetails.id,
        clientId: consumerDetails.clientId,
        bundleId: selectedBundle,
        returnUrlSuccess: `${window.location.origin}/messages?payment=success`,
        returnUrlFail: `${window.location.origin}/messages?payment=failed`,
        returnUrlAbort: `${window.location.origin}/messages?payment=abort`,
      };
      
      const result = await consumersApi.createPaymentOrder(payload);
      console.log('Create order result:', result);
      
      // Redirect to Inveroak's booking URL for payment
      if (result && result.bookingUrl) {
        console.log('Redirecting to booking URL:', result.bookingUrl);
        window.location.href = result.bookingUrl;
      } else if (result && result.success === false) {
        setOrderError(result.message || "Failed to create payment order");
        setCurrentState("payment-failed");
      } else {
        setOrderError("Invalid response from payment service");
        setCurrentState("payment-failed");
      }
    } catch (err: any) {
      console.error('Payment order creation failed:', err);
      setOrderError(err.message || "Payment initiation failed");
      setCurrentState("payment-failed");
    }
    setIsProcessing(false);
  };

  return (
    <Dialog
      open={showPurchaseModal}
      onClose={() => setShowPurchaseModal(false)}
      maxWidth="xs"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          minWidth: isMobile ? 'auto' : 400,
          maxWidth: isMobile ? '100%' : 440,
          minHeight: isMobile ? '100%' : 430,
          p: 1,
          position: "relative",
        },
      }}
    >
      <IconButton
        aria-label="close"
        onClick={() => setShowPurchaseModal(false)}
        size="small"
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: "#AB2C29",
          zIndex: 1,
        }}
      >
        <Cancel fontSize="small" />
      </IconButton>
      {currentState === "loading" && (
        <DialogContent
          sx={{
            pt: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box mb={2}>
            <Typography align="center" variant="h5" gutterBottom>
              {bundleDetails
                ? `${bundleDetails.credits} Credits - ${formatCurrencyAmount(bundleDetails.amount, bundleDetails.currency)}`
                : "Processing..."}
            </Typography>
          </Box>
          <Box m={4} height={40}>
            <div className="loader" />
          </Box>
          <Box mb={2}>
            <Box mb={2}>
              <Typography variant="h6" textAlign="center">
                <strong>Redirecting to secure payment page...</strong>
              </Typography>
              <Typography
                variant="body1"
                textAlign="center"
                fontSize={"14px"}
                gutterBottom
              >
                Please wait while we prepare your payment.
              </Typography>
              <Typography
                variant="body1"
                textAlign="center"
                fontSize={"14px"}
                gutterBottom
              >
                Please do not refresh or navigate away from this page
              </Typography>
            </Box>
          </Box>
        </DialogContent>
      )}
      {currentState === "purchase-credit" && (
        <DialogTitle sx={{ pr: 4 }}>
          <span style={{ color: "#a12a22", fontSize: 18 }}>
            PURCHASE CREDIT
          </span>
        </DialogTitle>
      )}
      {currentState === "purchase-credit" && (
        <DialogContent sx={{ pt: 1 }}>
          <Box mb={2}>
            <Typography>Credit Bundle</Typography>
            <Select
              value={selectedBundle}
              onChange={(e) => setSelectedBundle(e.target.value)}
              fullWidth
              sx={{ maxWidth: "240px" }}
            >
              {creditBundles.length === 0 && (
                <MenuItem disabled>No bundles available</MenuItem>
              )}
              {creditBundles.map((bundle: any) => (
                <MenuItem key={bundle.id} value={bundle.id}>
                  {bundle.credits} Credits - {formatCurrencyAmount(bundle.amount, bundle.currency)}
                </MenuItem>
              ))}
            </Select>
          </Box>
          <Box mb={3}>
            <Box sx={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: isMobile ? 1 : 4 }}>
              <Typography variant="body1" fontSize={isMobile ? "13px" : "14px"} gutterBottom>
                <strong>Name:</strong> <br /> {consumerDetails.firstName}{" "}
                {consumerDetails.surname}
              </Typography>
              <Typography variant="body1" fontSize={isMobile ? "13px" : "14px"} gutterBottom>
                <strong>Email:</strong> <br /> {consumerDetails.email}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: isMobile ? 1 : 4 }}>
              <Typography variant="body1" fontSize={isMobile ? "13px" : "14px"} gutterBottom>
                <strong>Address:</strong> <br /> {consumerDetails.address}{" "}
                <br />
                {consumerDetails.city}, {consumerDetails.state}, <br />
                {consumerDetails.country}, {consumerDetails.postcode}
              </Typography>
              <Typography variant="body1" fontSize={isMobile ? "13px" : "14px"} gutterBottom>
                <strong>Phone:</strong> <br /> {consumerDetails.telephone}
              </Typography>
            </Box>
            <Box mb={2}>
              <Typography variant="body1" fontSize={isMobile ? "13px" : "14px"} gutterBottom>
                <strong>Previously Used Credit Card </strong>
              </Typography>
              <Typography variant="body1" fontSize={isMobile ? "13px" : "14px"} gutterBottom>
                {savedCards.length > 0 
                  ? `${savedCards[0].cardType} **** **** **** ${savedCards[0].cardSuffix}`
                  : "**** **** **** ----"
                }
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                flexWrap: "wrap",
                gap: 2,
                mt: 2,
                width: "100%",
                position: isMobile ? "relative" : "absolute",
                bottom: isMobile ? "auto" : 36,
                left: isMobile ? "auto" : 0,
                justifyContent: "center",
                px: isMobile ? 0 : 2,
              }}
            >
              <Button
                variant="contained"
                onClick={handleUsePreviousCard}
                disabled={isProcessing || savedCards.length === 0}
                fullWidth={isMobile}
                sx={{
                  backgroundColor: "#a12a22",
                  "&:hover": { backgroundColor: "#b71c1c" },
                  "&:disabled": { backgroundColor: "#ccc" },
                  fontSize: isMobile ? 14 : 'inherit',
                }}
              >
                {savedCards.length === 0 ? "No Saved Card" : "Use Previous Card"}
              </Button>
              <Button
                variant="contained"
                onClick={handlePayWithNewCard}
                disabled={isProcessing}
                fullWidth={isMobile}
                sx={{
                  backgroundColor: "#a12a22",
                  "&:hover": { backgroundColor: "#b71c1c" },
                  paddingX: isMobile ? 2 : 5,
                  fontSize: isMobile ? 14 : 'inherit',
                }}
              >
                {isProcessing ? "Processing..." : "Use New Card"}
              </Button>
            </Box>
          </Box>
          {orderError && (
            <Box mt={2} sx={{ textAlign: "center" }}>
              <Typography color="error" variant="body2">
                {orderError}
              </Typography>
              <Button
                variant="outlined"
                onClick={() => {
                  setOrderError("");
                  setCurrentState("purchase-credit");
                }}
                sx={{ mt: 1, color: "#a12a22", borderColor: "#a12a22" }}
              >
                Try Again
              </Button>
            </Box>
          )}
        </DialogContent>
      )}
      {/* Payment result pages */}
      {currentState === "payment-success" && (
        <DialogContent
          sx={{
            pt: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            mb={1}
            sx={{ display: "flex", justifyContent: "center", padding: 2 }}
          >
            <Box
              sx={{
                border: "8px solid #198754",
                borderRadius: "50%",
                padding: 1,
              }}
            >
              <Check
                sx={{
                  width: "60px",
                  height: "60px",
                  color: "#198754",
                }}
              />
            </Box>
          </Box>
          <Box mb={2}>
            <Typography align="center" variant="h5" gutterBottom>
              Payment Successful
            </Typography>
            <Typography align="center" variant="body1" gutterBottom>
              {paymentResult?.creditsPurchased
                ? `${paymentResult.creditsPurchased} Credit${
                    paymentResult.creditsPurchased > 1 ? "s" : ""
                  } has been added to your account.`
                : "Credits have been added to your account."}
            </Typography>
          </Box>
        </DialogContent>
      )}
      {currentState === "payment-failed" && (
        <DialogContent
          sx={{
            pt: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            mb={1}
            sx={{ display: "flex", justifyContent: "center", padding: 2 }}
          >
            <Box
              sx={{
                border: "8px solid #dc3545",
                borderRadius: "50%",
                padding: 1,
              }}
            >
              <Clear
                sx={{
                  width: "60px",
                  height: "60px",
                  color: "#dc3545",
                }}
              />
            </Box>
          </Box>
          <Box mb={2}>
            <Typography align="center" variant="h5" gutterBottom>
              Payment Failed
            </Typography>
            <Typography align="center" variant="body1" gutterBottom>
              Please{" "}
              {
                <button
                  style={{
                    border: "none",
                    background: "none",
                    color: "#1976d2",
                    cursor: "pointer",
                    fontSize: "1rem",
                    borderBottom: "1px solid #1976d2",
                  }}
                  onClick={() => setCurrentState("purchase-credit")}
                >
                  try again
                </button>
              }{" "}
              or use a different payment method.
            </Typography>
          </Box>
        </DialogContent>
      )}
    </Dialog>
  );
}
